import { useCallback, useEffect, useState } from 'react'
import type {
  RiskReductionMeasure,
  SafetyFunction,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SafetyFunctionSelect } from './SafetyFunctionSelect'

type AssignSafetyFunctionDialogProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (safetyFunctionId: string | null) => void
  riskReductionMeasure: RiskReductionMeasure | null
  safetyFunctions: SafetyFunction[]
  isSaving?: boolean
}

export function AssignSafetyFunctionDialog({
  isOpen,
  onClose,
  onSave,
  riskReductionMeasure,
  safetyFunctions,
  isSaving = false,
}: AssignSafetyFunctionDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [selectedSafetyFunctionId, setSelectedSafetyFunctionId] = useState<
    string | null
  >(null)

  useEffect(() => {
    if (isOpen && riskReductionMeasure != null) {
      setSelectedSafetyFunctionId(riskReductionMeasure.safetyFunctionId ?? null)
    }
  }, [isOpen, riskReductionMeasure])

  const handleClose = useCallback(() => {
    if (riskReductionMeasure != null) {
      setSelectedSafetyFunctionId(riskReductionMeasure.safetyFunctionId ?? null)
    }
    onClose()
  }, [onClose, riskReductionMeasure])

  const handleSave = useCallback(() => {
    onSave(selectedSafetyFunctionId)
  }, [onSave, selectedSafetyFunctionId])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isSaving) {
        handleClose()
      }
    },
    [handleClose, isSaving]
  )

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className='sm:max-w-lg'
        showCloseButton={!isSaving}
        onEscapeKeyDown={(event) => {
          if (isSaving) {
            event.preventDefault()
          }
        }}
        onInteractOutside={(event) => {
          if (isSaving) {
            event.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t('riskReductionMeasuresLibraryPage.assignDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <SafetyFunctionSelect
          value={selectedSafetyFunctionId}
          onChange={setSelectedSafetyFunctionId}
          safetyFunctions={safetyFunctions}
          disabled={isSaving}
        />

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {t('riskReductionMeasuresLibraryPage.assignDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
